import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';

import MemberEligibilityBadges from '@/components/MemberEligibilityBadges';
import PersonSelect from '@/components/select/PersonSelect';
import SessionAttendanceChips, {
  getSessionSelectionSummary,
  SessionChipOption
} from '@/components/SessionAttendanceChips';
import FieldError from '@/forms/FieldError';
import { TreatmentGroupFormValues } from '@/forms/TreatmentGroupForm/types';
import useOtherGroupMembership from '@/hooks/useOtherGroupMembership';
import { MemberEligibilityStatus } from '@/types/treatmentGroup';
import { Pill } from '@/ui';
import { formatCalendarDate } from '@/utils/dates';

interface Props {
  autoFocus?: boolean;
  control: Control<TreatmentGroupFormValues>;
  currentGroupId?: string;
  errors: FieldErrors<TreatmentGroupFormValues>;
  index: number;
  // An existing member's real, currently-persisted session selection (see
  // TreatmentGroupForm) -- seeds the initial chip display so it reflects
  // their actual state, without living in the form itself (that would turn
  // an untouched "attends every session" member into an explicit list that
  // silently excludes any session added later). Undefined for a member
  // just added in this edit session.
  initialAttendingSessionIds?: string[];
  isEndedGroup: boolean;
  membershipId?: string;
  onRemove: () => void;
  personId: string | null;
  sessionOptions: SessionChipOption[];
  startDate: string | null;
  status?: MemberEligibilityStatus;
}

// A person's active membership in another group must be looked up
// individually (the endpoint takes one person id), so this row calls its own
// hook rather than the form batching it alongside the eligibility check.
const MemberRow = ({
  autoFocus,
  control,
  currentGroupId,
  errors,
  index,
  initialAttendingSessionIds,
  isEndedGroup,
  membershipId,
  onRemove,
  personId,
  sessionOptions,
  startDate,
  status
}: Props) => {
  const { data: otherMemberships } = useOtherGroupMembership(personId, currentGroupId);
  const defaultSessionIds = initialAttendingSessionIds ?? sessionOptions.map((s) => s.id);
  // Untouched, the field stays undefined (omitted on submit, so the backend
  // leaves this member's real selection alone) and displays
  // initialAttendingSessionIds -- their actual current selection for an
  // existing member, or every session for one just added in this edit
  // session. Once touched, the field holds an explicit array and that
  // takes over.
  const selectedSessionIds =
    useWatch({ control, name: `memberships.${index}.groupSessionIds` }) ?? defaultSessionIds;
  const sessionSummary = getSessionSelectionSummary(selectedSessionIds, sessionOptions);

  return (
    <div className="rounded-lg border border-uniform-gray-200 bg-white p-4 dark:border-uniform-gray-700 dark:bg-uniform-gray-900">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <Controller
            name={`memberships.${index}.person`}
            control={control}
            rules={{ validate: (value) => !!value || 'Member is required' }}
            render={({ field: { onChange, value } }) => (
              <PersonSelect
                ariaLabel="Member"
                autoFocus={autoFocus}
                showLabel={false}
                selectedOption={value}
                onChange={onChange}
                disabled={isEndedGroup || !!membershipId}
              />
            )}
          />
          <FieldError message={errors.memberships?.[index]?.person?.message} />
          {startDate && (
            <p className="mt-1 text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
              Member since {formatCalendarDate(startDate)}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {sessionOptions.length > 0 && (
            <Pill
              color={sessionSummary.isAllSelected ? 'neutral' : 'primary'}
              size="sm"
              variant="subtle">
              {sessionSummary.label}
            </Pill>
          )}
          {!isEndedGroup && (
            <button
              type="button"
              aria-label="Remove member"
              onClick={onRemove}
              className="text-uniform-gray-400 hover:text-uniform-red-600">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1">
        <MemberEligibilityBadges
          ineligibilityReason={status?.ineligibility_reason}
          isCredentialed={status?.is_credentialed}
          isEligible={status?.is_eligible}
          isExisting={!!membershipId}
          otherGroupNames={otherMemberships?.map((m) => m.treatment_group_name)}
        />
      </div>

      {sessionOptions.length > 0 && (
        <div className="mt-3 border-t border-dashed border-uniform-gray-200 pt-3 dark:border-uniform-gray-700">
          <Controller
            name={`memberships.${index}.groupSessionIds`}
            control={control}
            rules={{
              validate: (value) =>
                value === undefined || value.length > 0 || 'Select at least one session'
            }}
            render={({ field: { onChange, value } }) => (
              <SessionAttendanceChips
                disabled={isEndedGroup}
                label={
                  <span className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                    Attending Sessions
                  </span>
                }
                onChange={onChange}
                selectedIds={value ?? defaultSessionIds}
                sessions={sessionOptions}
              />
            )}
          />
          <FieldError message={errors.memberships?.[index]?.groupSessionIds?.message} />
        </div>
      )}
    </div>
  );
};

export default MemberRow;
